import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { PetServices } from '../../../../services/petservices';
import { DeviceDetectorService } from '../../../../services/device-detector.service';
import { ClientsService } from '../../../../services/clients.service';
import { PointOfSaleService } from '../../../../services/pos';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap, takeUntil, tap } from 'rxjs/operators';

type OpeningDay = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';

interface BookingOpeningShift {
  start: string;
  end: string;
}

interface BookingDaySchedule {
  day: OpeningDay;
  enabled: boolean;
  shifts: BookingOpeningShift[];
}

@Component({
  selector: 'app-newbooking',
  templateUrl: './newbooking.component.html',
  styleUrl: './newbooking.component.scss'
})
export class NewbookingComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  bookingForm!: FormGroup;

  contentReady = false;
  submitted = false;
  isMobile = false;
  isAr = false;

  services: any[] = [];
  serverError = '';
  clientLookupLoading = false;
  clientLookupMessage = '';
  scheduleLoading = false;
  scheduleError = '';
  existingBookings: any[] = [];
  clinicSchedule: BookingDaySchedule[] = [];
  bookingDateOnly = '';
  bookingTime = '10:00';
  showBookingCalendar = false;
  bookingCalendarViewDate = new Date();

  warehouseId: string | null = null;
  todayDateTime = this.formatDateTimeForInput(new Date());
  todayDateOnly = this.formatDateOnly(new Date());
  readonly hourOptions = Array.from({ length: 12 }, (_, index) => index + 1);
  readonly minuteOptions = Array.from({ length: 60 }, (_, index) =>
    index.toString().padStart(2, '0')
  );
  private readonly bookingDurationMinutes = 30;
  private readonly dayOrder: OpeningDay[] = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private deviceService: DeviceDetectorService,
    private petServices: PetServices,
    private clientsService: ClientsService,
    private posService: PointOfSaleService
  ) {}

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobileDevice();
    this.isAr = localStorage.getItem('language') === 'ar';
    this.warehouseId = localStorage.getItem('posId');

    this.bookingForm = this.fb.group({
      pet_service_id: ['', Validators.required],
      client_name: ['', Validators.required],
      phone_number: ['', [Validators.required, this.realEgyptOrKsaPhoneValidator]],
      pet_type: [''],
      booking_date: ['', Validators.required],

      // Admin booking
      from_website: ['admin'],
      status: [1],

      // Important: خليها null عشان مايتسجلش الحجز باسم الأدمن نفسه
      user_id: [null],

      warehouse_id: [this.warehouseId]
    });

    this.loadServices();
    this.loadScheduleAndBookings();
    this.watchClientPhone();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadServices(): void {
    this.contentReady = true;
    this.serverError = '';

    this.petServices.getAll(1, 1000).subscribe({
      next: (res) => {
        this.services = this.extractList(res);
        this.contentReady = false;
      },
      error: (err) => {
        this.contentReady = false;
        this.serverError = this.isAr
          ? 'حصل خطأ أثناء تحميل الخدمات'
          : 'Failed to load services';

        console.error(err);
      }
    });
  }

  private extractList(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data?.data)) return res.data.data;
    if (Array.isArray(res?.data)) return res.data;
    return [];
  }

  loadScheduleAndBookings(): void {
    if (!this.warehouseId) return;

    this.scheduleLoading = true;
    this.scheduleError = '';

    this.posService.getOpeningHours(this.warehouseId).subscribe({
      next: (res: any) => {
        this.clinicSchedule = this.normalizeSchedule(this.extractScheduleFromResponse(res));
        this.scheduleLoading = false;
        this.syncBookingDateTime();
      },
      error: (error) => {
        this.scheduleLoading = false;
        this.scheduleError =
          error?.error?.message ||
          (this.isAr ? 'تعذر تحميل مواعيد العيادة' : 'Failed to load clinic working hours');
      },
    });

    this.petServices.getAllBookings(1, 1000, this.warehouseId).subscribe({
      next: (res: any) => {
        this.existingBookings = this.extractBookings(res);
      },
      error: (error) => {
        console.error('Failed to load bookings:', error);
      },
    });
  }

  private watchClientPhone(): void {
    this.bookingForm
      .get('phone_number')
      ?.valueChanges.pipe(
        debounceTime(450),
        distinctUntilChanged(),
        tap(() => {
          this.clientLookupMessage = '';
        }),
        filter((value) => this.normalizePhone(value).length >= 8),
        tap(() => {
          this.clientLookupLoading = true;
        }),
        switchMap((value) =>
          this.clientsService
            .searchClients(String(value).trim(), Number(this.warehouseId) || undefined)
            .pipe(
              catchError((error) => {
                console.error('Failed to search client by phone:', error);
                return of(null);
              })
            )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((response) => {
        this.clientLookupLoading = false;
        const phone = this.bookingForm.get('phone_number')?.value || '';
        const client = this.findMatchedClient(response, phone);

        if (!client) return;

        this.applyExistingClient(client);
      });
  }

  private findMatchedClient(response: any, phone: string): any | null {
    const clients = this.extractClients(response);
    const normalizedPhone = this.normalizePhone(phone);

    if (!normalizedPhone) return null;

    return (
      clients.find((client) =>
        [
          client?.phone_number,
          client?.phone,
          client?.another_phone,
          client?.mobile,
        ].some((clientPhone) => this.normalizePhone(clientPhone) === normalizedPhone)
      ) || null
    );
  }

  private extractClients(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.clients)) return response.clients;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.clients?.data)) return response.clients.data;
    if (Array.isArray(response?.data?.clients)) return response.data.clients;
    if (Array.isArray(response?.data?.data)) return response.data.data;

    return [];
  }

  private applyExistingClient(client: any): void {
    const clientName =
      client?.client_name ||
      client?.name ||
      client?.user?.name ||
      this.bookingForm.get('client_name')?.value ||
      '';
    const phone =
      client?.phone_number ||
      client?.phone ||
      this.bookingForm.get('phone_number')?.value ||
      '';

    this.bookingForm.patchValue(
      {
        client_name: clientName,
        phone_number: phone,
      },
      { emitEvent: false }
    );

    this.clientLookupMessage = this.isAr
      ? 'تم تحميل بيانات العميل تلقائيًا'
      : 'Client data loaded automatically';
  }

  private normalizePhone(value: any): string {
    let phone = String(value ?? '').replace(/\D/g, '');

    if (phone.startsWith('0020')) phone = `0${phone.slice(4)}`;
    if (phone.startsWith('20') && phone.length === 12) phone = `0${phone.slice(2)}`;
    if (phone.startsWith('00966')) phone = `0${phone.slice(5)}`;
    if (phone.startsWith('966') && phone.length === 12) phone = `0${phone.slice(3)}`;

    return phone;
  }

  getServiceName(service: any): string {
    return this.isAr
      ? (
          service?.name_ar ||
          service?.title_ar ||
          service?.service_name_ar ||
          service?.name_en ||
          service?.title_en ||
          service?.service_name_en ||
          service?.name ||
          service?.title ||
          '-'
        )
      : (
          service?.name_en ||
          service?.title_en ||
          service?.service_name_en ||
          service?.name_ar ||
          service?.title_ar ||
          service?.service_name_ar ||
          service?.name ||
          service?.title ||
          '-'
        );
  }

  onBookingDateChange(event: Event): void {
    this.bookingDateOnly = (event.target as HTMLInputElement).value;
    this.syncBookingDateTime();
  }

  toggleBookingCalendar(): void {
    this.showBookingCalendar = !this.showBookingCalendar;

    if (this.showBookingCalendar) {
      const selectedDate = this.parseDateOnly(this.bookingDateOnly);
      const baseDate = selectedDate || new Date();
      this.bookingCalendarViewDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
    }
  }

  closeBookingCalendar(): void {
    this.showBookingCalendar = false;
  }

  previousBookingCalendarMonth(): void {
    this.bookingCalendarViewDate = new Date(
      this.bookingCalendarViewDate.getFullYear(),
      this.bookingCalendarViewDate.getMonth() - 1,
      1
    );
  }

  nextBookingCalendarMonth(): void {
    this.bookingCalendarViewDate = new Date(
      this.bookingCalendarViewDate.getFullYear(),
      this.bookingCalendarViewDate.getMonth() + 1,
      1
    );
  }

  getBookingCalendarTitle(): string {
    return new Intl.DateTimeFormat(this.isAr ? 'ar-EG' : 'en-US', {
      month: 'long',
      year: 'numeric',
    }).format(this.bookingCalendarViewDate);
  }

  getBookingCalendarWeekDays(): string[] {
    return this.isAr
      ? ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
      : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  }

  getBookingCalendarDays(): Array<{
    date: Date;
    dayNumber: string;
    isCurrentMonth: boolean;
    isToday: boolean;
    isSelected: boolean;
    isDisabled: boolean;
  }> {
    const year = this.bookingCalendarViewDate.getFullYear();
    const month = this.bookingCalendarViewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const gridStart = new Date(year, month, 1 - firstDay.getDay());
    const today = this.parseDateOnly(this.todayDateOnly) || new Date();
    today.setHours(0, 0, 0, 0);

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);
      date.setHours(0, 0, 0, 0);

      return {
        date,
        dayNumber: this.isAr ? this.toArabicDigits(date.getDate()) : String(date.getDate()),
        isCurrentMonth: date.getMonth() === month,
        isToday: this.isSameDate(date, today),
        isSelected: this.bookingDateOnly === this.formatDateOnly(date),
        isDisabled: date < today,
      };
    });
  }

  selectBookingCalendarDate(date: Date): void {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    const today = this.parseDateOnly(this.todayDateOnly) || new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate < today) return;

    this.bookingDateOnly = this.formatDateOnly(selectedDate);
    this.syncBookingDateTime();
    this.closeBookingCalendar();
  }

  getBookingDateDisplay(): string {
    const date = this.parseDateOnly(this.bookingDateOnly);
    if (!date) {
      return this.isAr ? 'اختار تاريخ الحجز' : 'Select booking date';
    }

    return new Intl.DateTimeFormat(this.isAr ? 'ar-EG' : 'en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }

  updateBookingTimePart(part: 'hour' | 'minute' | 'period', event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    const [hourText, minuteText] = (this.bookingTime || '10:00').split(':');
    const currentHour = Number(hourText);
    let hour12 = this.getTimeHour12(this.bookingTime);
    let minute = minuteText || '00';
    let period: 'am' | 'pm' = !Number.isNaN(currentHour) && currentHour >= 12 ? 'pm' : 'am';

    if (part === 'hour') {
      hour12 = Number(value);
    }

    if (part === 'minute') {
      minute = value.padStart(2, '0');
    }

    if (part === 'period') {
      period = value as 'am' | 'pm';
    }

    this.bookingTime = `${this.to24Hour(hour12, period)}:${minute}`;
    this.syncBookingDateTime();
  }

  isSelectedBookingTimeValid(): boolean {
    if (!this.bookingDateOnly || !this.bookingTime) return false;

    return this.isTimeWithinClinicHours(this.bookingDateOnly, this.bookingTime) &&
      !this.isBookingTimeTaken(this.bookingDateOnly, this.bookingTime);
  }

  getBookingTimeError(): string {
    if (!this.bookingDateOnly || !this.bookingTime) return '';

    if (!this.isTimeWithinClinicHours(this.bookingDateOnly, this.bookingTime)) {
      return this.isAr
        ? 'الميعاد خارج مواعيد عمل العيادة'
        : 'This time is outside clinic working hours';
    }

    if (this.isBookingTimeTaken(this.bookingDateOnly, this.bookingTime)) {
      return this.isAr
        ? 'في حجز موجود في نفس الوقت'
        : 'There is already a booking at this time';
    }

    return '';
  }

  formatTime(time: string): string {
    if (!time) return '';

    const [hourText, minuteText] = time.split(':');
    const hour = Number(hourText);
    const minute = Number(minuteText);

    if (Number.isNaN(hour) || Number.isNaN(minute)) return time;

    const hour12 = hour % 12 || 12;
    const formattedTime = `${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

    if (!this.isAr) {
      return `${formattedTime} ${hour < 12 ? 'AM' : 'PM'}`;
    }

    return `${this.toArabicDigits(formattedTime)} ${hour < 12 ? 'صباحًا' : 'مساءً'}`;
  }

  getTimeHour12(time: string): number {
    const hour = Number((time || '10:00').split(':')[0]);
    return Number.isNaN(hour) ? 10 : hour % 12 || 12;
  }

  getTimeMinute(time: string): string {
    const minute = (time || '10:00').split(':')[1] || '00';
    return minute.padStart(2, '0');
  }

  getTimePeriod(time: string): 'am' | 'pm' {
    const hour = Number((time || '10:00').split(':')[0]);
    return !Number.isNaN(hour) && hour >= 12 ? 'pm' : 'am';
  }

  getTimeOptionLabel(value: string | number): string {
    return this.isAr ? this.toArabicDigits(value) : String(value);
  }

  getPeriodOptionLabel(period: 'am' | 'pm'): string {
    if (!this.isAr) return period.toUpperCase();
    return period === 'am' ? 'صباحًا' : 'مساءً';
  }

  realEgyptOrKsaPhoneValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const rawValue = String(control.value).trim();
    const arabicRegex = /[\u0600-\u06FF\u0660-\u0669\u06F0-\u06F9]/;

    if (arabicRegex.test(rawValue)) {
      return { arabicNotAllowed: true };
    }

    const phone = rawValue.replace(/[\s\-()]/g, '');

    const egyptMobileRegex = /^(01[0125]\d{8}|\+201[0125]\d{8}|00201[0125]\d{8})$/;
    const ksaMobileRegex = /^(05\d{8}|\+9665\d{8}|009665\d{8})$/;

    return egyptMobileRegex.test(phone) || ksaMobileRegex.test(phone)
      ? null
      : { invalidEgyptOrKsaPhone: true };
  };

  shouldShowError(controlName: string): boolean {
    const control = this.bookingForm?.get(controlName);
    return !!control && control.invalid && (control.touched || this.submitted);
  }

  getErrorMessage(controlName: string, label: string): string {
    const control = this.bookingForm?.get(controlName);

    if (!control || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return this.isAr ? `${label} مطلوب` : `${label} is required`;
    }

    if (control.errors['invalidEgyptOrKsaPhone']) {
      return this.isAr
        ? `${label} لازم يكون رقم موبايل مصري أو سعودي صحيح`
        : `${label} must be a valid Egyptian or Saudi mobile number`;
    }

    if (control.errors['arabicNotAllowed']) {
      return this.isAr
        ? `${label} لازم يكون بأرقام إنجليزية بدون حروف عربية`
        : `${label} must use English digits without Arabic characters`;
    }

    return this.isAr ? `${label} غير صحيح` : `${label} is invalid`;
  }

  getMissingRequiredCount(): number {
    if (!this.bookingForm) {
      return 0;
    }

    return Object.keys(this.bookingForm.controls).filter((controlName) => {
      const control = this.bookingForm.get(controlName);
      return control?.hasError('required');
    }).length;
  }

  private validateBeforeSubmit(): boolean {
    this.submitted = true;
    this.serverError = '';

    if (this.bookingForm.invalid) {
      this.bookingForm.markAllAsTouched();

      Swal.fire({
        icon: 'warning',
        title: this.isAr ? 'تأكد من بيانات الحجز' : 'Please check booking data',
        text: this.isAr
          ? 'راجع الرسائل تحت الحقول المطلوبة أو غير الصحيحة'
          : 'Review the messages below the required or invalid fields',
        confirmButtonText: this.isAr ? 'تمام' : 'OK',
      });

      return false;
    }

    if (!this.isSelectedBookingTimeValid()) {
      this.bookingForm.get('booking_date')?.markAsTouched();

      Swal.fire({
        icon: 'warning',
        title: this.isAr ? 'ميعاد غير متاح' : 'Unavailable booking time',
        text: this.getBookingTimeError(),
        confirmButtonText: this.isAr ? 'تمام' : 'OK',
      });

      return false;
    }

    return true;
  }

  saveBooking(): void {
    if (!this.validateBeforeSubmit()) {
      return;
    }

    const raw = this.bookingForm.getRawValue();

    const payload = {
      pet_service_id: Number(raw.pet_service_id),
      client_name: raw.client_name,
      phone_number: raw.phone_number,
      pet_type: raw.pet_type || null,
      booking_date: this.normalizeDateTime(raw.booking_date),
      from_website: 'admin',
      status: Number(raw.status ?? 1),
      user_id: raw.user_id || null,
      warehouse_id: raw.warehouse_id || this.warehouseId || null
    };

    this.contentReady = true;

    this.petServices.createBooking(payload).subscribe({
      next: () => {
        this.contentReady = false;

        Swal.fire({
          icon: 'success',
          title: this.isAr ? 'تم إنشاء الحجز بنجاح' : 'Booking created successfully',
          confirmButtonText: this.isAr ? 'تمام' : 'OK',
        });

        this.router.navigate(['/clientsbookings']);
      },
      error: (err) => {
        this.contentReady = false;

        this.serverError =
          err?.error?.message ||
          (this.isAr ? 'حصل خطأ أثناء إنشاء الحجز' : 'Failed to create booking');

        Swal.fire({
          icon: 'error',
          title: this.isAr ? 'خطأ' : 'Error',
          text: this.serverError,
          confirmButtonText: this.isAr ? 'تمام' : 'OK',
        });

        console.error(err);
      }
    });
  }

  resetBookingForm(): void {
    this.submitted = false;
    this.serverError = '';
    this.clientLookupLoading = false;
    this.clientLookupMessage = '';

    this.bookingForm.reset({
      pet_service_id: '',
      client_name: '',
      phone_number: '',
      pet_type: '',
      booking_date: '',
      from_website: 'admin',
      status: 1,
      user_id: null,
      warehouse_id: this.warehouseId
    });
    this.bookingDateOnly = '';
    this.bookingTime = '10:00';
  }

  private syncBookingDateTime(): void {
    if (!this.bookingDateOnly) {
      this.bookingForm.patchValue({ booking_date: '' }, { emitEvent: false });
      return;
    }

    this.bookingForm.patchValue(
      {
        booking_date: `${this.bookingDateOnly}T${this.bookingTime}`,
      },
      { emitEvent: false }
    );
  }

  private isTimeWithinClinicHours(dateText: string, time: string): boolean {
    const date = this.parseDateOnly(dateText);
    if (!date) return false;

    const daySchedule = this.clinicSchedule.find((item) => item.day === this.dayOrder[date.getDay()]);
    if (!daySchedule?.enabled || !daySchedule.shifts.length) return false;

    const selectedMinutes = this.toMinutes(time);

    return daySchedule.shifts.some((shift) => {
      const start = this.toMinutes(shift.start);
      const end = this.toMinutes(shift.end);

      if (end > start) {
        return selectedMinutes >= start && selectedMinutes < end;
      }

      return selectedMinutes >= start || selectedMinutes < end;
    });
  }

  private isBookingTimeTaken(dateText: string, time: string): boolean {
    const selectedDate = this.parseDateOnly(dateText);
    if (!selectedDate) return false;

    const selectedStart = this.toMinutes(time);
    const selectedEnd = selectedStart + this.bookingDurationMinutes;

    return this.existingBookings.some((booking) => {
      const bookingDate = this.parseBookingDate(booking?.booking_date || booking?.deferred_booking_date);
      if (!bookingDate || !this.isSameDate(bookingDate, selectedDate)) return false;

      if (this.isBookingCanceled(booking?.status)) return false;

      const bookingStart = bookingDate.getHours() * 60 + bookingDate.getMinutes();
      const bookingEnd = bookingStart + this.bookingDurationMinutes;

      return selectedStart < bookingEnd && selectedEnd > bookingStart;
    });
  }

  private isBookingCanceled(status: any): boolean {
    const normalizedStatus = String(status ?? '').trim().toLowerCase();

    return ['2', 'rejected', 'cancelled', 'canceled', 'cancel'].includes(normalizedStatus);
  }

  private extractBookings(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data?.data)) return res.data.data;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.bookings)) return res.bookings;
    return [];
  }

  private extractScheduleFromResponse(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data?.schedule)) return res.data.schedule;
    if (Array.isArray(res?.schedule)) return res.schedule;
    if (Array.isArray(res?.opening_hours)) return res.opening_hours;
    if (Array.isArray(res?.data?.opening_hours)) return res.data.opening_hours;
    if (Array.isArray(res?.data)) return res.data;
    return [];
  }

  private normalizeSchedule(apiSchedule: any[]): BookingDaySchedule[] {
    return this.dayOrder.map((day) => {
      const dayRows = apiSchedule.filter((row) => row?.day === day);
      const firstRow = dayRows[0];

      if (!firstRow) {
        return { day, enabled: false, shifts: [] };
      }

      const isOpen = this.toBoolean(firstRow.is_open);
      const is24Hours = this.toBoolean(firstRow.is_24_hours ?? firstRow.is24hours ?? false);
      const shifts = dayRows.flatMap((row) => this.extractShiftsFromRow(row)).filter((shift) => shift.start && shift.end);

      return {
        day,
        enabled: isOpen,
        shifts: isOpen
          ? is24Hours
            ? [{ start: '00:00', end: '23:59' }]
            : shifts
          : [],
      };
    });
  }

  private extractShiftsFromRow(row: any): BookingOpeningShift[] {
    const shiftsFromJson = this.parseShifts(row?.shifts);
    if (shiftsFromJson.length > 0) return shiftsFromJson;

    if (row?.opens_at && row?.closes_at) {
      return [
        {
          start: this.normalizeTime(row.opens_at),
          end: this.normalizeTime(row.closes_at),
        },
      ];
    }

    return [];
  }

  private parseShifts(shifts: any): BookingOpeningShift[] {
    if (!shifts) return [];

    let parsed = shifts;
    if (typeof shifts === 'string') {
      try {
        parsed = JSON.parse(shifts);
      } catch {
        return [];
      }
    }

    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((shift) => ({
        start: this.normalizeTime(shift?.start),
        end: this.normalizeTime(shift?.end),
      }))
      .filter((shift) => shift.start && shift.end);
  }

  private normalizeTime(time: string | null | undefined): string {
    if (!time) return '';
    const match = String(time).match(/^(\d{1,2}):(\d{2})/);
    if (!match) return '';
    return `${String(Number(match[1])).padStart(2, '0')}:${match[2]}`;
  }

  private toBoolean(value: any): boolean {
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  private to24Hour(hour12: number, period: 'am' | 'pm'): string {
    let hour = Number(hour12);
    if (period === 'am' && hour === 12) hour = 0;
    if (period === 'pm' && hour !== 12) hour += 12;
    return String(hour).padStart(2, '0');
  }

  private toMinutes(time: string): number {
    const [hour, minute] = time.split(':').map(Number);
    return hour * 60 + minute;
  }

  private parseDateOnly(value: string): Date | null {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  private parseBookingDate(value: any): Date | null {
    if (!value) return null;
    const cleanValue = String(value).replace('T', ' ').replace('Z', '').split('.')[0].trim();
    const match = cleanValue.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}):(\d{2}))?/);
    if (!match) return null;
    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4] || 0),
      Number(match[5] || 0)
    );
  }

  private isSameDate(first: Date, second: Date): boolean {
    return first.getFullYear() === second.getFullYear() &&
      first.getMonth() === second.getMonth() &&
      first.getDate() === second.getDate();
  }

  private normalizeDateTime(value: string): string {
    if (!value) return value;

    // datetime-local بيرجع YYYY-MM-DDTHH:mm
    // Laravel يقبلها، بس بنزود الثواني عشان تبقى أوضح
    return value.length === 16 ? `${value}:00` : value;
  }

  private formatDateTimeForInput(date: Date): string {
    const pad = (num: number) => String(num).padStart(2, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  private formatDateOnly(date: Date): string {
    const pad = (num: number) => String(num).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private toArabicDigits(value: string | number): string {
    return String(value).replace(/\d/g, (digit) => '٠١٢٣٤٥٦٧٨٩'[Number(digit)]);
  }
}
